/* eslint-disable no-console */
import {
  hasAnySubCategory,
  hasSubCategory,
  type IdValue
} from '../../../utils/companyCategorySubCategory';

import {
  cbamSection1ExporterAndInstallationProfileQuestions,
  cbamSection2ActivityDataAndProductionSystemBoundaryQuestions,
  cbamSection3DirectEmbeddedEmissionsScope1Questions,
  cbamSection4IndirectEmbeddedEmissionsElectricityQuestions,
  cbamSection5PrecursorEmissionsQuestions,
  cbamSection6MethodologyDataQualityVerificationQuestions,
  cbamSection7EmissionSummaryAndExportSpecificOutputQuestions
} from './cbam/cbamObj';
import {getCbamEffectiveSubCategory} from './cbam/cbamSubCategoryOverride';
import {
  cbamSection1IronAndSteelPlantAndProductionInformationQuestions,
  cbamSection2IronAndSteelSourceStreamAndDirectEmissionsDataQuestions,
  cbamSection3IronAndSteelElectricityAndIndirectEmissionsQuestions,
  cbamSection4IronAndSteelProcessLevelProductionAndEmissionsDataQuestions,
  cbamSection5IronAndSteelPrecursorPurchasedInputEmissionsDataQuestions,
  cbamSection6IronAndSteelCarbonPricingAndTaxAdjustmentQuestions,
  cbamSection7IronAndSteelShipmentAndCustomsInformationQuestions
} from './cbam/IronandSteel/ironandSteelObj';
import {
  corporateDisclosureTabQuestions,
  corporateEnvironmentalPerformanceBiodiversityTabQuestions,
  corporateEnvironmentalPerformanceFinancialServicesTabQuestions,
  corporateEnvironmentalPerformanceForestsTabQuestions,
  corporateEnvironmentalPerformancePlasticsTabQuestions,
  corporateEnvironmentalPerformanceWaterSecurityTabQuestions,
  corporateFurtherInformationAndSignOffTabQuestions,
  corporateGovernanceTabQuestions,
  corporateIntroTabQuestions,
  corporateManagementTabQuestions,
  corporatePerformanceClimateChangeTabQuestions,
  corporatePerformanceConsolidationApproachTabQuestions,
  corporateStrategyTabQuestions
} from './cdp/corporate/corporateObj';
import {
  smeBusinessStrategyTabQuestions,
  smeDisclosureTabQuestions,
  smeGovernanceTabQuestions,
  smeIntroTabQuestions,
  smeManagementTabQuestions,
  smePerformanceClimateChangeTabQuestions,
  smePerformanceConsolidationApproachTabQuestions,
  smeSignOffTabQuestions
} from './cdp/sme/smeObj';
import {environmental} from './environmental/envObj';
import {
  cbamIronAndSteelSubCategories,
  frameworkRoute,
  pillarRoutes,
  subCategoryConstants
} from './frameworkRoutes';
import {general} from './general/generalObj';
import {generalDisclosure} from './generalDisclosure/generalDisclosureObj';
import {governance} from './governance/governanceObj';
import {managementAndprocessDisclosure} from './managementAndProcessDisclosures/managementAndProcessDisclosuresObj';
import {principleWisePerformanceDisclosure} from './principleWisePerformanceDisclosure/principleWisePerformanceDisclosureObj';
import {realEstateObj} from './realEstate/realEstateObj';
import {social} from './social/socialObj';
import {
  autoMobileGovernance,
  autoMobileMetricsTargets,
  autoMobileRiskManagement,
  autoMobileStrategy
} from './tcfd/autoMobile/autoMobileObj';
import {
  generalGovernance,
  generalMetricsTargets,
  generalRiskManagement,
  generalStrategy
} from './tcfd/general/generalObj';
import {
  realEstateGovernance,
  realEstateMetricsTargets,
  realEstateRiskManagement,
  realEstateStrategy
} from './tcfd/realEstate/realEstateObj';

type FrameworkPillarRouteMapper = Record<string, Record<string, string>>;

export const frameworkPillarRouteMapper: FrameworkPillarRouteMapper = {
  [frameworkRoute.TCFD]: {
    governance: 'pillar_governance',
    strategy: 'pillar_strategy',
    riskManagement: 'pillar_risk_management',
    matricsAndTargets: 'pillar_metrics_targets'
  },
  [frameworkRoute.CDP_SME]: {
    [pillarRoutes.SME_INTRODUCTION]: 'pillar_sme_introduction',
    [pillarRoutes.SME_MANAGEMENT]: 'pillar_sme_management',
    [pillarRoutes.SME_DISCLOSURE]: 'pillar_sme_disclosure',
    [pillarRoutes.SME_GOVERNANCE]: 'pillar_sme_governance',
    [pillarRoutes.BUSINESS_STRATEGY]: 'pillar_sme_business_strategy',
    [pillarRoutes.PERFORMANCE_CONSOLIDATION_APPROACH]:
      'pillar_sme_performance_consolidation_approach',
    [pillarRoutes.PERFORMANCE_CLIMATE_CHANGE]:
      'pillar_sme_performance_climate_change',
    [pillarRoutes.SME_SIGN_OFF]: 'pillar_sme_sign_off'
  },
  [frameworkRoute.CDP_CORPORATE]: {
    [pillarRoutes.CORPORATE_INTRODUCTION]: 'pillar_corporate_introduction',
    [pillarRoutes.CORPORATE_MANAGEMENT]: 'pillar_corporate_management',
    [pillarRoutes.CORPORATE_DISCLOSURE]: 'pillar_corporate_disclosure',
    [pillarRoutes.CORPORATE_GOVERNANCE]: 'pillar_corporate_governance',
    [pillarRoutes.CORPORATE_STRATEGY]: 'pillar_corporate_strategy',
    [pillarRoutes.CORPORATE_PERFORMANCE_CONSOLIDATION_APPROACH]:
      'pillar_corporate_performance_consolidation_approach',
    [pillarRoutes.CORPORATE_PERFORMANCE_CLIMATE_CHANGE]:
      'pillar_corporate_performance_climate_change',
    [pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_FORESTS]:
      'pillar_corporate_environmental_performance_forests',
    [pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_WATER_SECURITY]:
      'pillar_corporate_environmental_performance_water_security',
    [pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_PLASTICS]:
      'pillar_corporate_environmental_performance_plastics',
    [pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_BIO_DIVERSITY]:
      'pillar_corporate_environmental_performance_biodiversity',
    [pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_FINANCIAL_SERVICES]:
      'pillar_corporate_environmental_performance_financial_services',
    [pillarRoutes.CORPORATE_FURTHER_INFORMATION_AND_SIGN_OFF]:
      'pillar_corporate_further_information_and_sign_off'
  },
  [frameworkRoute.CBAM]: {
    // CBAM iron & steel questionnaire pillar ids use `&` (see ironandSteelObj pillers).
    [pillarRoutes.SECTION_1_PLANT_AND_PRODUCTION_INFORMATION]:
      'section1Plant&ProductionInformation',
    [pillarRoutes.SECTION_2_SOURCE_STREAM_AND_DIRECT_EMISSIONS_DATA]:
      'section2SourceStream&DirectEmissionsData',
    [pillarRoutes.SECTION_3_ELECTRICITY_AND_INDIRECT_EMISSIONS]:
      'section3Electricity&IndirectEmissions',
    [pillarRoutes.SECTION_4_PROCESS_LEVEL_PRODUCTION_AND_EMISSIONS_DATA]:
      'section4ProcessLevelProduction&EmissionsData',
    [pillarRoutes.SECTION_5_PRECURSOR_PURCHASED_INPUT_EMISSIONS_DATA]:
      'section5PrecursorPurchasedInputEmissionsData',
    [pillarRoutes.SECTION_6_CARBON_PRICING_AND_TAX_ADJUSTMENT]:
      'section6CarbonPricing&TaxAdjustment',
    [pillarRoutes.SECTION_7_SHIPMENT_AND_CUSTOMS_INFORMATION]:
      'section7Shipment&CustomsInformation'
  }
};

export const mapFrameworkPillarRoute = (
  framework: string | undefined,
  routePart: string
): string => {
  if (!framework || !routePart) return routePart;

  const frameworkMap = frameworkPillarRouteMapper[framework];

  if (!frameworkMap) return routePart;

  return frameworkMap[routePart] ?? routePart;
};

export const returnQuestions: (
  framework: string | undefined,
  pillar: string | undefined,
  subCategory?: IdValue
) => any = (framework, pillar, subCategory) => {
  let pillarObj: any = {};
  switch (framework) {
    case frameworkRoute.GRI:
      switch (pillar) {
        case pillarRoutes.GENERAL:
          pillarObj = general;
          break;
        case pillarRoutes.ENV:
          pillarObj = environmental;
          break;
        case pillarRoutes.SOCIAL:
          pillarObj = social;
          break;
        case pillarRoutes.GOV:
          pillarObj = governance;
          break;
        default:
          pillarObj = undefined;
      }
      break;
    case frameworkRoute.BRSR:
      switch (pillar) {
        case pillarRoutes.SECTIONA:
          pillarObj = generalDisclosure;
          break;
        case pillarRoutes.SECTIONB:
          pillarObj = managementAndprocessDisclosure;
          break;
        case pillarRoutes.SECTIONC:
          pillarObj = principleWisePerformanceDisclosure;
          break;
        default:
          pillarObj = undefined;
      }
      break;
    case frameworkRoute.TCFD:
      if (hasSubCategory(subCategory, 2)) {
        switch (pillar) {
          case pillarRoutes.GOV:
            pillarObj = realEstateGovernance;
            break;
          case pillarRoutes.STARTEGY:
            pillarObj = realEstateStrategy;
            break;
          case pillarRoutes.RISK_MANAGEMENT:
            pillarObj = realEstateRiskManagement;
            break;
          case pillarRoutes.MATRICS_TARGETS:
            pillarObj = realEstateMetricsTargets;
            break;
          default:
            pillarObj = undefined;
        }
      } else if (hasSubCategory(subCategory, 5)) {
        switch (pillar) {
          case pillarRoutes.GOV:
            pillarObj = autoMobileGovernance;
            break;
          case pillarRoutes.STARTEGY:
            pillarObj = autoMobileStrategy;
            break;
          case pillarRoutes.RISK_MANAGEMENT:
            pillarObj = autoMobileRiskManagement;
            break;
          case pillarRoutes.MATRICS_TARGETS:
            pillarObj = autoMobileMetricsTargets;
            break;
          default:
            pillarObj = undefined;
        }
      } else {
        switch (pillar) {
          case pillarRoutes.GOV:
            pillarObj = generalGovernance;
            break;
          case pillarRoutes.STARTEGY:
            pillarObj = generalStrategy;
            break;
          case pillarRoutes.RISK_MANAGEMENT:
            pillarObj = generalRiskManagement;
            break;
          case pillarRoutes.MATRICS_TARGETS:
            pillarObj = generalMetricsTargets;
            break;
          default:
            pillarObj = undefined;
        }
      }
      break;
    case frameworkRoute.SASB:
      switch (pillar) {
        case pillarRoutes.REAL_ESTATE:
        case 'real_estate':
        case 'REAL_ESTATE':
          pillarObj = realEstateObj;
          break;
        default:
          pillarObj = undefined;
      }
      break;
    case frameworkRoute.CDP_SME:
      switch (pillar) {
        case pillarRoutes.SME_INTRODUCTION:
          pillarObj = smeIntroTabQuestions;
          break;
        case pillarRoutes.SME_MANAGEMENT:
          pillarObj = smeManagementTabQuestions;
          break;
        case pillarRoutes.SME_DISCLOSURE:
          pillarObj = smeDisclosureTabQuestions;
          break;
        case pillarRoutes.SME_GOVERNANCE:
          pillarObj = smeGovernanceTabQuestions;
          break;
        case pillarRoutes.BUSINESS_STRATEGY:
          pillarObj = smeBusinessStrategyTabQuestions;
          break;
        case pillarRoutes.PERFORMANCE_CONSOLIDATION_APPROACH:
          pillarObj = smePerformanceConsolidationApproachTabQuestions;
          break;
        case pillarRoutes.PERFORMANCE_CLIMATE_CHANGE:
          pillarObj = smePerformanceClimateChangeTabQuestions;
          break;
        case pillarRoutes.SME_SIGN_OFF:
          pillarObj = smeSignOffTabQuestions;
          break;
        default:
          pillarObj = undefined;
      }
      break;
    case frameworkRoute.CDP_CORPORATE:
      switch (pillar) {
        case pillarRoutes.CORPORATE_INTRODUCTION:
          pillarObj = corporateIntroTabQuestions;
          break;
        case pillarRoutes.CORPORATE_MANAGEMENT:
          pillarObj = corporateManagementTabQuestions;
          break;
        case pillarRoutes.CORPORATE_DISCLOSURE:
          pillarObj = corporateDisclosureTabQuestions;
          break;
        case pillarRoutes.CORPORATE_GOVERNANCE:
          pillarObj = corporateGovernanceTabQuestions;
          break;
        case pillarRoutes.CORPORATE_STRATEGY:
          pillarObj = corporateStrategyTabQuestions;
          break;
        case pillarRoutes.CORPORATE_PERFORMANCE_CONSOLIDATION_APPROACH:
          pillarObj = corporatePerformanceConsolidationApproachTabQuestions;
          break;
        case pillarRoutes.CORPORATE_PERFORMANCE_CLIMATE_CHANGE:
          pillarObj = corporatePerformanceClimateChangeTabQuestions;
          break;
        case pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_FORESTS:
          pillarObj = corporateEnvironmentalPerformanceForestsTabQuestions;
          break;
        case pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_WATER_SECURITY:
          pillarObj =
            corporateEnvironmentalPerformanceWaterSecurityTabQuestions;
          break;
        case pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_PLASTICS:
          pillarObj = corporateEnvironmentalPerformancePlasticsTabQuestions;
          break;
        case pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_BIO_DIVERSITY:
          pillarObj = corporateEnvironmentalPerformanceBiodiversityTabQuestions;
          break;
        case pillarRoutes.CORPORATE_ENVIRONMENTAL_PERFORMANCE_FINANCIAL_SERVICES:
          pillarObj =
            corporateEnvironmentalPerformanceFinancialServicesTabQuestions;
          break;
        case pillarRoutes.CORPORATE_FURTHER_INFORMATION_AND_SIGN_OFF:
          pillarObj = corporateFurtherInformationAndSignOffTabQuestions;
          break;
        default:
          pillarObj = undefined;
      }
      break;
    case frameworkRoute.CBAM:
      subCategory = getCbamEffectiveSubCategory(subCategory);
      if (hasAnySubCategory(subCategory, cbamIronAndSteelSubCategories)) {
        switch (pillar) {
          case pillarRoutes.SECTION_1_PLANT_AND_PRODUCTION_INFORMATION:
            pillarObj =
              cbamSection1IronAndSteelPlantAndProductionInformationQuestions;
            break;
          case pillarRoutes.SECTION_2_SOURCE_STREAM_AND_DIRECT_EMISSIONS_DATA:
            pillarObj =
              cbamSection2IronAndSteelSourceStreamAndDirectEmissionsDataQuestions;
            break;
          case pillarRoutes.SECTION_3_ELECTRICITY_AND_INDIRECT_EMISSIONS:
            pillarObj =
              cbamSection3IronAndSteelElectricityAndIndirectEmissionsQuestions;
            break;
          case pillarRoutes.SECTION_4_PROCESS_LEVEL_PRODUCTION_AND_EMISSIONS_DATA:
            pillarObj =
              cbamSection4IronAndSteelProcessLevelProductionAndEmissionsDataQuestions;
            break;
          case pillarRoutes.SECTION_5_PRECURSOR_PURCHASED_INPUT_EMISSIONS_DATA:
            pillarObj =
              cbamSection5IronAndSteelPrecursorPurchasedInputEmissionsDataQuestions;
            break;
          case pillarRoutes.SECTION_6_CARBON_PRICING_AND_TAX_ADJUSTMENT:
            pillarObj =
              cbamSection6IronAndSteelCarbonPricingAndTaxAdjustmentQuestions;
            break;
          case pillarRoutes.SECTION_7_SHIPMENT_AND_CUSTOMS_INFORMATION:
            pillarObj =
              cbamSection7IronAndSteelShipmentAndCustomsInformationQuestions;
            break;
          default:
            pillarObj = undefined;
        }
      }
      if (
        hasSubCategory(subCategory, subCategoryConstants.CEMENT_SUB_CATEGORY)
      ) {
        switch (pillar) {
          case pillarRoutes.SECTION_1_EXPORTER_AND_INSTALLATION_PROFILE:
            pillarObj = cbamSection1ExporterAndInstallationProfileQuestions;
            break;
          case pillarRoutes.SECTION_2_ACTIVITY_DATA_AND_PRODUCTION_SYSTEM_BOUNDARY:
            pillarObj =
              cbamSection2ActivityDataAndProductionSystemBoundaryQuestions;
            break;
          case pillarRoutes.SECTION_3_DIRECT_EMBEDDED_EMISSIONS_SCOPE_1:
            pillarObj = cbamSection3DirectEmbeddedEmissionsScope1Questions;
            break;
          case pillarRoutes.SECTION_4_INDIRECT_EMBEDDED_EMISSIONS_ELECTRICITY:
            pillarObj =
              cbamSection4IndirectEmbeddedEmissionsElectricityQuestions;
            break;
          case pillarRoutes.SECTION_5_PRECURSOR_EMISSIONS:
            pillarObj = cbamSection5PrecursorEmissionsQuestions;
            break;
          case pillarRoutes.SECTION_6_METHODOLOGY_DATA_QUALITY_VERIFICATION:
            pillarObj = cbamSection6MethodologyDataQualityVerificationQuestions;
            break;
          case pillarRoutes.SECTION_7_EMISSION_SUMMARY_AND_EXPORT_SPECIFIC_OUTPUT:
            pillarObj =
              cbamSection7EmissionSummaryAndExportSpecificOutputQuestions;
            break;
          default:
            pillarObj = undefined;
        }
      }
      break;
    default:
      pillarObj = undefined;
  }

  return pillarObj;
};
